import {Button, Modal, ModalContent, ModalFooter, ModalTitle} from '@hm/fundamental'
import Icon from '@mdi/react'
import React from 'react'

import {ErrorModalVariant} from '../../types/orderChangeResult.types'

import {useErrorModalContentConfig} from './useErrorModalContentConfig'

interface ErrorModalProps {
  /** Whether the modal is open */
  isOpen: boolean
  /** The variant of the error modal */
  variant: ErrorModalVariant | null
  /** Optional custom error message */
  message?: string
  /** Callback for primary action (retry/start over) */
  onPrimaryAction: () => void
  /** Callback for secondary action (go back/discard) */
  onSecondaryAction?: () => void
}

/**
 * ErrorModal Component
 *
 * Displays error modals with three variants:
 * - retryable: Server errors (500) - "Go back" and "Send it again" buttons
 * - nonRetryable: Client errors (400) - "Go back" button only
 * - checksum: Order modified conflict - "Discard request" (red) and "Start over" buttons
 */
export const ErrorModal: React.FC<ErrorModalProps> = ({
  isOpen,
  variant,
  message,
  onPrimaryAction,
  onSecondaryAction
}) => {
  const content = useErrorModalContentConfig(variant, message)

  if (!content) return null

  return (
    <Modal
      open={isOpen}
      onClose={onSecondaryAction ?? onPrimaryAction}
      width="thin"
      data-test-id="error-modal"
    >
      <ModalTitle className="flex items-center gap-2">
        <Icon path={content.icon} size={1} className={content.iconColor} />
        {content.title}
      </ModalTitle>

      <ModalContent>
        <div className="flex flex-col gap-2">
          <p className="text-sm text-gray-600">{content.description}</p>
          {content.secondaryDescription && (
            <p className="text-sm text-gray-600">{content.secondaryDescription}</p>
          )}
        </div>
      </ModalContent>

      <ModalFooter>
        <div className="flex w-full gap-3">
          {content.showSecondaryButton && onSecondaryAction && (
            <Button
              variant={content.secondaryButtonVariant}
              onClick={onSecondaryAction}
              className="flex-1"
              data-test-id="error-modal-secondary-button"
            >
              {content.secondaryButtonText}
            </Button>
          )}
          <Button
            variant="primary"
            onClick={onPrimaryAction}
            className={content.showSecondaryButton ? 'flex-1' : 'w-full'}
            data-test-id="error-modal-primary-button"
          >
            {content.primaryButtonText}
          </Button>
        </div>
      </ModalFooter>
    </Modal>
  )
}
